import { useCallback, useMemo } from 'react';

import _ from 'lodash';
import { useQueries, useQuery } from '@tanstack/react-query';

import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import { useTranslation } from 'localization/useTranslation';
import { mapDtdlToAssetModel } from 'components/Admin/Model/utils/model';

const mergeDesired = (model, desiredProps) => {
  const rootProps = rootPropsFn(model.properties);
  const rootMetaProps = rootPropsFn(model.metaProperties);
  const modules = _.map(model.modules, module => {
    const moduleProps = modulePropsFn(module.properties);
    const moduleMetaProps = modulePropsFn(module.metaProperties);
    return { ...module, properties: moduleProps, metaProperties: moduleMetaProps };

    function modulePropsFn(properties) {
      return _.map(properties, prop => {
        return { ...prop, desired: desiredProps?.[module.id]?.[prop.name] };
      });
    }
  });
  return { ...model, properties: rootProps, metaProperties: rootMetaProps, modules };

  function rootPropsFn(properties) {
    return _.map(properties, prop => {
      return { ...prop, desired: desiredProps?.[prop.name] };
    });
  }
};

const mergeReported = (model, reportedProps) => {
  const rootProps = rootPropsFn(model.properties);
  const rootMetaProps = rootPropsFn(model.metaProperties);
  const modules = _.map(model.modules, module => {
    const moduleProps = modulePropsFn(module.properties);
    const moduleMetaProps = modulePropsFn(module.metaProperties);
    return { ...module, properties: moduleProps, metaProperties: moduleMetaProps };

    function modulePropsFn(properties) {
      return _.map(properties, prop => {
        return { ...prop, reported: reportedProps?.[module.id]?.[prop.name] };
      });
    }
  });
  return { ...model, properties: rootProps, metaProperties: rootMetaProps, modules };

  function rootPropsFn(properties) {
    return _.map(properties, prop => {
      return { ...prop, reported: reportedProps?.[prop.name] };
    });
  }
};

const mergeCompability = (model, desiredProps, reportedProps) => {
  const rootProps = mergeRootProps(model.properties);
  const rootMetaProps = mergeRootProps(model.metaProperties);
  const modules = _.map(model.modules, module => {
    const moduleProps = mergeModuleProps(module.properties);
    const moduleMetaProps = mergeModuleProps(module.metaProperties);
    return { ...module, properties: moduleProps, metaProperties: moduleMetaProps };

    function mergeModuleProps(properties) {
      return _.map(properties, prop => {
        return { ...prop, desired: desiredProps?.[module.id]?.[prop.name], reported: reportedProps?.[module.id]?.[prop.name] };
      });
    }
  });
  function mergeRootProps(properties) {
    return _.map(properties, prop => {
      return { ...prop, desired: desiredProps?.[prop.name], reported: reportedProps?.[prop.name] };
    });
  }

  return { ...model, properties: rootProps, metaProperties: rootMetaProps, modules };
};

/**
 * Hook to get the properties of an asset
 * @param {string} assetId
 * @param {function} addSuccessAlert
 * @param {function} addErrorAlert
 * @returns {object} { asset, isUpdatingProps, updateProps, refetchReported, isLoading, assetModel }
 *
  */
export const useProperties = ({ assetId, addSuccessAlert, addErrorAlert }) => {
  const T = useTranslation();
  const api = useAPI();

  const assetQuery = useQuery([`asset-${assetId}`, assetId],
    createQueryRequest(api.core.getAsset),
    { enabled: !!assetId }
  );
  const asset = assetQuery?.data;
  // TODO: Update modelId location when API changes
  const modelId = asset?.modelId ?? asset?.details?.modelId;
  const allQueries = useQueries({
    queries: [
      {
        queryKey: [`desiredProps-${assetId}`, assetId],
        queryFn: createQueryRequest(api.core.getAssetDesiredProperties),
        enabled: !!assetId
      },
      {
        queryKey: [`reportedProps-${assetId}`, assetId],
        queryFn: createQueryRequest(api.core.getAssetReportedProperties),
        enabled: !!assetId
      },
      {
        queryKey: [`model-${modelId}`, modelId],
        queryFn: createQueryRequest(api.core.getModel),
        enabled: !!modelId,
        staleTime: Infinity, // models never change and can be cached forever
        cacheTime: Infinity
      }
    ]
  });

  const isLoading = _.some(allQueries, { isLoading: true }) || assetQuery.isLoading;
  // TODO: show error
  const desiredProps = allQueries[0].data;
  const reportedProps = allQueries[1].data;
  const model = allQueries[2].data;
  const assetModel = useMemo(() => {
    if (model) {
      let oldModel = mapDtdlToAssetModel(model.modelData[0], model.modelData);
      oldModel = mergeCompability(oldModel, desiredProps, reportedProps);
      oldModel = mergeDesired(oldModel, desiredProps);
      oldModel = mergeReported(oldModel, reportedProps);
      return oldModel;
    }
    return {};
  }, [desiredProps, model, reportedProps]);

  const [updateDesired, isUpdatingDesired] = useApiCall({
    apiCall: api.core.updateAssetDesiredProperties,
    onSuccess: useCallback(() => {
      addSuccessAlert(T.asset.updateDesiredSuccess);
      _.forEach(allQueries, (query) => query.refetch());
    }, [T.asset.updateDesiredSuccess, addSuccessAlert, allQueries]),
    onError: useCallback((error) => {
      addErrorAlert(T.asset.updateDesiredError);
      console.error('error', error);
    }, [T.asset.updateDesiredError, addErrorAlert])
  });

  const updateProps = useCallback((props) => {
    updateDesired(assetId, props);
  }, [assetId, updateDesired]);

  const refetchReported = useCallback(() => {
    _.forEach(allQueries, (query) => query.refetch());
  }, [allQueries]);

  return { asset, updateProps, isUpdatingProps: isUpdatingDesired, refetchReported, isLoading, assetModel };
};
